// Main menu (§9.1, §14.2): the playable diorama behind, the right column (QUICK PLAY biggest,
// CREATE ROOM, JOIN CODE, PRACTICE, then LOCKER, BEAN CARD, SETTINGS, HOW TO PLAY) and the footer
// (level badge + XP bar, Bonk Bucks, today's 3 challenges). The bean stays controllable: WASD,
// the mouse buttons, the left stick or the touch stick; arrows and the D-pad move menu focus.
// The logo hops to the lounge beat; first-time prompts teach the verbs on the diorama.
import type { LiveScreen } from '../contracts';
import type { UiDeps } from '../deps';
import { actionCap } from '../glyphView';
import { beanIcon, icon, identityHex } from '../icons';
import { el, setStyle, setText } from '../ui';
import { createPromptView } from '../hud/PromptView';
import { button, logo } from '../widgets';

export function create(deps: UiDeps): LiveScreen {
  const profiles = deps.services.profiles;
  const name = el('span', { class: 'pc-name ellipsis' });
  const levelNum = el('span', { class: 'lvl-num' });
  const xpFill = el('i');
  const xpText = el('span', { class: 'pc-xp' });
  const bucks = el('span', { class: 'bucks-num' });
  const avatar = el('div', { class: 'pc-avatar' });
  const challenges = el('div', { class: 'pc-challenges', attrs: { 'aria-label': "Today's challenges" } });
  const hintBox = el('div', { class: 'menu-hint', attrs: { 'aria-hidden': 'true' } });
  const prompt = createPromptView(deps, 'menu-prompt');

  const quick = button({ label: 'QUICK PLAY', sub: 'Jump in now • bots fill to 8', icon: 'play', variant: 'pink', size: 'big', cls: 'menu-quick', autofocus: true, onClick: () => deps.actions.quickPlay() });
  const create = button({ label: 'CREATE ROOM', icon: 'door', variant: 'sky', cls: 'menu-btn', onClick: () => deps.nav.show('createRoom') });
  const join = button({ label: 'JOIN CODE', icon: 'key', cls: 'menu-btn', onClick: () => deps.nav.show('joinCode') });
  const practice = button({ label: 'PRACTICE', icon: 'target', variant: 'mint', cls: 'menu-btn', onClick: () => deps.nav.show('practice') });
  const tile = (label: string, ic: string, target: 'locker' | 'beanCard' | 'settings' | 'howToPlay'): HTMLButtonElement =>
    el('button', { class: 'tile', attrs: { type: 'button', 'aria-label': label }, on: { click: () => deps.nav.show(target) } },
      el('span', { class: 'tile-ico' }, icon(ic)), el('span', { class: 'tile-lbl', text: label }));

  const col = el('nav', { class: 'menu-col', attrs: { 'aria-label': 'Main menu' } },
    el('div', { class: 'menu-logo' }, logo('THE BEAN-BONKING PARTY BRAWLER')),
    quick,
    el('div', { class: 'menu-grid' }, create, join, practice),
    el('div', { class: 'menu-tiles' },
      tile('LOCKER', 'shirt', 'locker'), tile('BEAN CARD', 'card', 'beanCard'),
      tile('SETTINGS', 'gear', 'settings'), tile('HOW TO PLAY', 'question', 'howToPlay')));

  const card = el('button', { class: 'player-card', attrs: { type: 'button', 'aria-label': 'Open your Bean Card' }, on: { click: () => deps.nav.show('beanCard') } },
    avatar,
    el('div', { class: 'pc-main' },
      el('div', { class: 'pc-top' }, el('span', { class: 'lvl-badge' }, el('small', { text: 'LV' }), levelNum), name),
      el('div', { class: 'bar xp pc-bar' }, xpFill),
      el('div', { class: 'pc-meta' }, xpText, el('span', { class: 'coin-count' }, icon('coin'), bucks))));

  const footer = el('div', { class: 'menu-footer' }, card, challenges);

  function renderHints(): void {
    const s = (label: string, ...caps: HTMLElement[]): HTMLElement => el('span', { class: 'mh' }, ...caps, el('span', { class: 'mh-l', text: label }));
    hintBox.replaceChildren(
      el('span', { class: 'mh-title' }, icon('sparkle'), el('b', { text: 'YOUR BEAN IS LIVE!' })),
      el('span', { class: 'mh-row' }, s('move', actionCap(deps, 'move')), s('shove', actionCap(deps, 'shove')), s('grab', actionCap(deps, 'grab')), s('jump', actionCap(deps, 'jump'))));
  }
  deps.services.input.onChange(() => {
    if (!root.hidden) renderHints();
  });

  function render(): void {
    const p = deps.profile();
    setText(name, p.name || 'New Bean');
    const lv = profiles.level();
    setText(levelNum, String(lv.level));
    setStyle(xpFill, 'width', `${Math.round(lv.frac * 100)}%`);
    setText(xpText, lv.capped ? 'MAX LEVEL' : `${lv.xp} / ${lv.need} XP`);
    setText(bucks, String(p.bucks));
    const pref = p.cosmetics.identityPref < 8 ? p.cosmetics.identityPref : 0;
    avatar.replaceChildren(deps.services.portrait(p.cosmetics, pref, 128) ?? beanIcon(identityHex(pref, false)));
    const list = profiles.challenges();
    challenges.replaceChildren(
      el('div', { class: 'pc-ch-head' }, icon('flag'), el('span', { text: "TODAY'S CHALLENGES" })),
      ...list.map((c) => el('div', { class: `pc-ch${c.done ? ' done' : ''}` },
        el('span', { class: 'pc-ch-box' }, c.done ? icon('check') : null),
        el('span', { class: 'pc-ch-text ellipsis', text: c.text }),
        el('span', { class: 'pc-ch-prog', text: c.done ? 'DONE' : `${c.progress}/${c.goal}` }))));
  }
  deps.bus.on('profile', () => {
    if (!root.hidden) render();
  });

  const root = el('div', { class: 'screen menu', attrs: { 'data-noback': true } }, hintBox, prompt.el, col, footer);
  /** A portrait phone has no room for the diorama's move stick beside the menu: hide it there. */
  const syncTouch = (): void => {
    if (root.hidden) return;
    deps.services.input.touch.setMode(window.innerHeight > window.innerWidth ? 'hidden' : 'auto');
  };
  window.addEventListener('resize', syncTouch);
  return {
    el: root,
    onShow() {
      render();
      renderHints();
      syncTouch();
    },
    onHide() {
      deps.services.input.touch.setMode('auto');
    },
    update(view) {
      prompt.update(view);
    },
  };
}
